import { formatAustralianDate } from "./generateSnapshot";
import type { FrictionSnapshot } from "../types/friction-finder";

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN = 54;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

type PdfFont = "F1" | "F2";

const winAnsiSpecialCharacters = new Map<number, number>([
  [0x2018, 0x91], [0x2019, 0x92], [0x201c, 0x93], [0x201d, 0x94],
  [0x2022, 0x95], [0x2013, 0x96], [0x2014, 0x97], [0x2026, 0x85],
]);

function winAnsiHex(value: string) {
  return Array.from(value.replace(/\s+/g, " ").trim()).map((character) => {
    const codePoint = character.codePointAt(0) ?? 0x3f;
    const byte = codePoint <= 0x7f || (codePoint >= 0xa0 && codePoint <= 0xff)
      ? codePoint
      : winAnsiSpecialCharacters.get(codePoint) ?? 0x3f;
    return byte.toString(16).padStart(2, "0");
  }).join("").toUpperCase();
}

function characterWidth(character: string, bold: boolean) {
  if (character === " ") return 0.278;
  if (/[ilI.,'`:;!|]/.test(character)) return 0.278;
  if (/[MW@%&]/.test(character)) return bold ? 0.91 : 0.86;
  if (/[A-Z]/.test(character)) return bold ? 0.69 : 0.66;
  if (/[0-9]/.test(character)) return 0.556;
  if (/[-–—()\[\]{}]/.test(character)) return 0.36;
  return bold ? 0.57 : 0.52;
}

function estimatedWidth(value: string, fontSize: number, bold = false) {
  return Array.from(value).reduce((width, character) => width + characterWidth(character, bold) * fontSize, 0);
}

function wrapText(value: string, fontSize: number, maxWidth: number, bold = false) {
  const words = value.replace(/\s+/g, " ").trim().split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && estimatedWidth(candidate, fontSize, bold) > maxWidth) {
      lines.push(current);
      current = word;
    } else current = candidate;
  }
  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

function pdfDate(input: string) {
  const date = new Date(input);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `D:${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
}

export function snapshotPdfFilename(snapshot: FrictionSnapshot) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Adelaide",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(snapshot.generatedAt)).map((part) => [part.type, part.value]));
  return `huddleco-friction-snapshot-${parts.year}-${parts.month}-${parts.day}.pdf`;
}

export function createSnapshotPdfBytes(snapshot: FrictionSnapshot) {
  const pages: string[][] = [[]];
  let pageIndex = 0;
  let y = PAGE_HEIGHT - MARGIN;

  const commands = () => pages[pageIndex];
  const addPage = () => {
    pages.push([]);
    pageIndex += 1;
    y = PAGE_HEIGHT - MARGIN;
    addLine("HuddleCo", "F2", 11, 15, "0.180 0.408 0.302");
    addLine("Friction Snapshot continued", "F1", 8.5, 14, "0.325 0.380 0.357");
    y -= 12;
  };
  const ensureSpace = (height: number) => { if (y - height < MARGIN) addPage(); };
  function addLine(text: string, font: PdfFont, fontSize: number, lineHeight: number, colour = "0.090 0.129 0.114") {
    ensureSpace(lineHeight);
    commands().push(`${colour} rg BT /${font} ${fontSize.toFixed(2)} Tf 1 0 0 1 ${MARGIN.toFixed(2)} ${y.toFixed(2)} Tm <${winAnsiHex(text)}> Tj ET`);
    y -= lineHeight;
  }
  const addBlock = (text: string, options: { font?: PdfFont; fontSize?: number; lineHeight?: number; before?: number; after?: number; colour?: string } = {}) => {
    const font = options.font ?? "F1";
    const fontSize = options.fontSize ?? 10.5;
    const lineHeight = options.lineHeight ?? 15.5;
    const lines = wrapText(text, fontSize, CONTENT_WIDTH, font === "F2");
    ensureSpace((options.before ?? 0) + lines.length * lineHeight + (options.after ?? 0));
    y -= options.before ?? 0;
    for (const line of lines) addLine(line, font, fontSize, lineHeight, options.colour);
    y -= options.after ?? 0;
  };
  const addSection = (heading: string, statement: string) => {
    ensureSpace(84);
    y -= 14;
    commands().push(`0.835 0.867 0.847 RG 0.75 w ${MARGIN.toFixed(2)} ${y.toFixed(2)} m ${(PAGE_WIDTH - MARGIN).toFixed(2)} ${y.toFixed(2)} l S`);
    y -= 24;
    addBlock(heading, { font: "F2", fontSize: 12, lineHeight: 15, colour: "0.180 0.408 0.302", after: 8 });
    addBlock(statement, { fontSize: 10.5, lineHeight: 16, colour: "0.203 0.255 0.231" });
  };

  addLine("HuddleCo", "F2", 14, 35, "0.090 0.129 0.114");
  addBlock(snapshot.title, { font: "F2", fontSize: 28, lineHeight: 32, after: 12 });
  addBlock(snapshot.intent, { font: "F2", fontSize: 11, lineHeight: 14, colour: "0.180 0.408 0.302", after: 20 });
  addBlock(snapshot.summary, { fontSize: 12, lineHeight: 18, colour: "0.090 0.129 0.114" });
  addSection(snapshot.impactHeading, snapshot.impactStatement);
  addSection(snapshot.desiredStateHeading, snapshot.desiredStateStatement);
  addSection(snapshot.nextStepHeading, snapshot.nextStepStatement);
  addBlock(`Generated ${formatAustralianDate(snapshot.generatedAt)}`, { fontSize: 9, lineHeight: 12, before: 28, colour: "0.325 0.380 0.357" });

  const objectBodies: string[] = [];
  objectBodies.push("<< /Type /Catalog /Pages 2 0 R >>");
  objectBodies.push("");
  objectBodies.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>");
  objectBodies.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>");
  const pageObjectIds: number[] = [];
  for (const pageCommands of pages) {
    const stream = pageCommands.join("\n");
    const contentObjectId = objectBodies.length + 1;
    objectBodies.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    const pageObjectId = objectBodies.length + 1;
    pageObjectIds.push(pageObjectId);
    objectBodies.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH.toFixed(2)} ${PAGE_HEIGHT.toFixed(2)}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObjectId} 0 R >>`);
  }
  objectBodies[1] = `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageObjectIds.length} >>`;
  const infoObjectId = objectBodies.length + 1;
  objectBodies.push(`<< /Title <${winAnsiHex("HuddleCo Friction Snapshot")}> /Author <${winAnsiHex("HuddleCo")}> /Creator <${winAnsiHex("HuddleCo Friction Finder")}> /CreationDate (${pdfDate(snapshot.generatedAt)}) >>`);

  let pdf = "%PDF-1.4\n%HuddleCo\n";
  const offsets = [0];
  objectBodies.forEach((body, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objectBodies.length + 1}\n0000000000 65535 f \n`;
  pdf += offsets.slice(1).map((offset) => `${String(offset).padStart(10, "0")} 00000 n \n`).join("");
  pdf += `trailer\n<< /Size ${objectBodies.length + 1} /Root 1 0 R /Info ${infoObjectId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  return new TextEncoder().encode(pdf);
}

export function createSnapshotPdf(snapshot: FrictionSnapshot) {
  return new Blob([createSnapshotPdfBytes(snapshot)], { type: "application/pdf" });
}

export function downloadSnapshotPdf(snapshot: FrictionSnapshot) {
  const blob = createSnapshotPdf(snapshot);
  const filename = snapshotPdfFilename(snapshot);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.hidden = true;
  document.body.append(link);
  try {
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
  return { blob, filename };
}
